import { useState, useEffect, useRef, useCallback } from 'react';

// Regex to detect ISO 8601 date strings produced by JSON.stringify(new Date())
const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

/**
 * JSON.parse reviver that restores Date objects serialised by JSON.stringify.
 * Handles null values and multiline string fields (description, patients[].description)
 * transparently — they are plain strings and survive the round-trip unchanged.
 */
function dateReviver(_key, value) {
  if (typeof value === 'string' && ISO_DATE_REGEX.test(value)) {
    const d = new Date(value);
    return isNaN(d.getTime()) ? value : d;
  }
  return value;
}

/**
 * useInvoiceDraft
 *
 * Debounced localStorage autosave for invoice form state.
 * Works for both the Monthly Invoice (entries, rateType, rateValue, selectedPractice)
 * and the Daily Record Log (date, hours, rateType, rateValue, description, patients,
 * selectedPractice) flows.
 *
 * Features:
 * - Saves to localStorage after `debounceMs` of inactivity (default 2 s)
 * - Restores Date objects on load (JSON.stringify / parse round-trip safe)
 * - Multi-line description strings survive the round-trip unchanged
 * - No-op when storageKey is null/undefined (e.g. user not yet loaded)
 * - Returns { savedDraft, clearDraft, dismissDraft }
 *
 * @param {string|null} storageKey  Unique localStorage key per user/flow.
 *                                  e.g. `invoice-monthly-${user.uid}`
 * @param {object}      draft       Current form state snapshot to save.
 * @param {number}      [debounceMs=2000]  Debounce delay in ms.
 * @param {function|null} [hasContent=null]  Optional predicate — return true when
 *                                  the form has data worth saving (e.g. any entry
 *                                  has a date or hours). When the predicate returns
 *                                  false the autosave timer is never started, which
 *                                  means a blank/reset form can never create a
 *                                  phantom draft — regardless of what other code
 *                                  does.
 */
export function useInvoiceDraft(storageKey, draft, debounceMs = 2000, hasContent = null) {
  const [savedDraft, setSavedDraft] = useState(null);
  const timerRef = useRef(null);
  // Skip the very first save so an empty/default form doesn't overwrite an
  // existing draft before the user has had a chance to restore it.
  const skipNextSave = useRef(true);
  // After a successful invoice save (clearDraft), track the cleared content so
  // we never re-save it even if the component re-renders (e.g. entry expand/collapse).
  // Autosave resumes normally once the user makes a genuine edit.
  const clearedDraftJsonRef = useRef(null);
  // Always holds the latest draftJson so clearDraft can capture it without
  // needing draftJson as a useCallback dependency.
  const latestDraftJsonRef = useRef(null);

  // ── Read existing draft on mount (or when storageKey becomes available) ──
  useEffect(() => {
    if (!storageKey) return;
    const raw = localStorage.getItem(storageKey);
    if (raw) {
      try {
        const parsed = JSON.parse(raw, dateReviver);
        setSavedDraft(parsed);
      } catch {
        // Corrupt data — remove silently
        localStorage.removeItem(storageKey);
      }
    }
    // Reset the skip flag whenever the key changes (e.g. user switches account)
    skipNextSave.current = true;
  }, [storageKey]);

  // ── Debounced auto-save whenever draft content changes ──
  // JSON.stringify(draft) is used as the dependency value so the effect only
  // re-runs when the actual data changes, not just on new object references.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const draftJson = JSON.stringify(draft);
  latestDraftJsonRef.current = draftJson; // keep in sync with latest render

  useEffect(() => {
    if (!storageKey) return;

    // Primary guard: if the caller says there's nothing worth saving (e.g. blank
    // form after "New Invoice"), skip entirely — no timer, no localStorage write.
    // This makes phantom drafts structurally impossible: a blank form can never
    // autosave regardless of how many re-renders the reset triggers.
    if (hasContent && !hasContent(draft)) return;

    if (skipNextSave.current) {
      skipNextSave.current = false;
      return;
    }

    // Don't re-save content that was just explicitly cleared (clearDraft flow).
    // Once the user makes a genuine edit (draftJson differs from cleared baseline),
    // clear the guard and allow normal autosaving to resume.
    if (clearedDraftJsonRef.current !== null) {
      if (draftJson === clearedDraftJsonRef.current) {
        return;
      }
      clearedDraftJsonRef.current = null; // user changed something — re-enable
    }

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      try {
        localStorage.setItem(storageKey, draftJson);
      } catch (e) {
        // Quota exceeded or storage unavailable — fail silently
        console.warn('[useInvoiceDraft] Could not save draft:', e.message);
      }
    }, debounceMs);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, draftJson, debounceMs]);

  /** Call after a successful save to Firestore — clears the autosave draft. */
  const clearDraft = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (storageKey) localStorage.removeItem(storageKey);
    setSavedDraft(null);
    // Remember the cleared content so the autosave effect never re-saves it
    // even if the component re-renders with identical data (e.g. entry expand/collapse).
    // Autosave resumes normally once the user makes a genuine edit.
    clearedDraftJsonRef.current = latestDraftJsonRef.current;
  }, [storageKey]);

  /** Call when the user clicks "Dismiss" on the restore banner. */
  const dismissDraft = useCallback(() => {
    if (storageKey) localStorage.removeItem(storageKey);
    setSavedDraft(null);
  }, [storageKey]);

  /**
   * Call after restoring a draft into the form. Only hides the banner —
   * deliberately does NOT touch localStorage. Deleting it here would rely on
   * the debounced autosave re-writing the (now-current) content afterwards,
   * which is lost if the user navigates away again before the debounce fires.
   * The real localStorage entry is only cleared by clearDraft() (on actual save)
   * or dismissDraft() (explicit discard).
   */
  const acknowledgeDraft = useCallback(() => {
    setSavedDraft(null);
  }, []);

  /**
   * Suppress the very next autosave trigger.
   * Use when resetting the form to a blank state (e.g. "New Invoice") so the
   * empty/reset state is not immediately written to localStorage as a draft.
   * Normal autosaving resumes on the subsequent state change.
   */
  const pauseSave = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    skipNextSave.current = true;
  }, []);

  return { savedDraft, clearDraft, dismissDraft, acknowledgeDraft, pauseSave };
}
